export type LegacySelfServePlanType = "starter" | "pro" | "max";
export type PublicSelfServePlanType = "starter_v2" | "pro_v2" | "max_v2";
export type BillingPlanType =
  | LegacySelfServePlanType
  | PublicSelfServePlanType
  | "enterprise";
export type SelfServePlanType =
  | LegacySelfServePlanType
  | PublicSelfServePlanType;
export type BillingPlanCatalogFamily = "legacy" | "new" | "enterprise";

export const BILLING_PLAN_LABELS = {
  starter: "Starter",
  pro: "Pro",
  max: "Max",
  starter_v2: "Starter",
  pro_v2: "Pro",
  max_v2: "Max",
  enterprise: "A medida",
} as const satisfies Record<BillingPlanType, string>;

export const BILLING_PLAN_OPERATOR_LABELS = {
  starter: "Starter (legacy)",
  pro: "Pro (legacy)",
  max: "Max (legacy)",
  starter_v2: "Starter (nuevo)",
  pro_v2: "Pro (nuevo)",
  max_v2: "Max (nuevo)",
  enterprise: "A medida",
} as const satisfies Record<BillingPlanType, string>;

export function getBillingPlanLabel(planType: BillingPlanType): string {
  return BILLING_PLAN_LABELS[planType];
}

export function getBillingPlanOperatorLabel(planType: BillingPlanType): string {
  return BILLING_PLAN_OPERATOR_LABELS[planType];
}

export function getBillingPlanCatalogFamily(
  planType: BillingPlanType
): BillingPlanCatalogFamily {
  if (planType === "enterprise") {
    return "enterprise";
  }
  return planType.endsWith("_v2") ? "new" : "legacy";
}

export const PUBLIC_PLAN_REFS = {
  c7n4q2: "starter_v2",
  r9m5x8: "pro_v2",
  h3k7v6: "max_v2",
} as const satisfies Record<string, PublicSelfServePlanType>;

const STALE_PUBLIC_PLAN_REFS = {
  st4r69: "starter_v2",
  xk7m9n: "pro_v2",
  w3j8r5: "max_v2",
} as const satisfies Record<string, PublicSelfServePlanType>;

export const PUBLIC_PLAN_TO_REF = {
  starter_v2: "c7n4q2",
  pro_v2: "r9m5x8",
  max_v2: "h3k7v6",
} as const satisfies Record<PublicSelfServePlanType, string>;

export type PublicPlanRef = keyof typeof PUBLIC_PLAN_REFS;
export type StalePublicPlanRef = keyof typeof STALE_PUBLIC_PLAN_REFS;

export function getPlanFromRef(
  ref: string | null
): PublicSelfServePlanType | null {
  if (!ref) {
    return null;
  }

  return (
    PUBLIC_PLAN_REFS[ref as PublicPlanRef] ??
    STALE_PUBLIC_PLAN_REFS[ref as StalePublicPlanRef] ??
    null
  );
}

export function getRefFromPlan(plan: PublicSelfServePlanType): string {
  return PUBLIC_PLAN_TO_REF[plan];
}

export type BillingPeriod = "monthly" | "annual";

export const PROMOTIONAL_CONSTANTS = {
  trialDays: 10,
  promotionalDiscount: 0.3,
  promotionalMonths: 3,
  annualDiscount: 0.25,
} as const;

export function isMonthlyPromotionEligible(
  planType: SelfServePlanType
): boolean {
  return (
    planType === "pro" ||
    planType === "max" ||
    planType === "starter_v2" ||
    planType === "pro_v2" ||
    planType === "max_v2"
  );
}

export function getPromotionalPrice(
  monthlyPrice: number,
  planType: SelfServePlanType
): number {
  if (!isMonthlyPromotionEligible(planType)) {
    return monthlyPrice;
  }

  return Math.round(
    monthlyPrice * (1 - PROMOTIONAL_CONSTANTS.promotionalDiscount)
  );
}

export const ALL_PLANS_FEATURES = [
  "Te ayudamos a migrar tus datos",
  "Implementación incluida",
  "Soporte por WhatsApp o videollamada",
  "API + MCP incluidos",
] as const;

export const LEGACY_PRICING_TIERS = [
  {
    name: "starter",
    label: "Starter",
    promotionalPrice: getPromotionalPrice(81_900, "starter"),
    monthlyPromotionEligible: isMonthlyPromotionEligible("starter"),
    regularPrice: 81_900,
    users: 2,
    locations: 1,
    invoices: 500,
    integrations: 1,
    exclusive: false,
    description: "Para negocios que están empezando",
  },
  {
    name: "pro",
    label: "Pro",
    promotionalPrice: getPromotionalPrice(163_900, "pro"),
    monthlyPromotionEligible: isMonthlyPromotionEligible("pro"),
    regularPrice: 163_900,
    users: 5,
    locations: 3,
    invoices: 2500,
    integrations: 3,
    exclusive: false,
    description: "Para negocios en crecimiento",
  },
  {
    name: "max",
    label: "Max",
    promotionalPrice: getPromotionalPrice(269_900, "max"),
    monthlyPromotionEligible: isMonthlyPromotionEligible("max"),
    regularPrice: 269_900,
    users: 10,
    locations: 5,
    invoices: 5000,
    integrations: 5,
    exclusive: true,
    description: "Para empresas en crecimiento",
    features: [
      "Contabilidad y centros de costo",
      "Multimoneda: ARS y USD",
      "Campos personalizados de gestión",
    ],
  },
] as const;

export const PUBLIC_PRICING_TIERS = [
  {
    name: "starter_v2",
    label: "Starter",
    promotionalPrice: getPromotionalPrice(119_900, "starter_v2"),
    monthlyPromotionEligible: isMonthlyPromotionEligible("starter_v2"),
    regularPrice: 119_900,
    users: 3,
    locations: 1,
    monthlySalesCommercialLimit: 500,
    integrations: 2,
    cuits: 1,
    exclusive: false,
    description: "Para ordenar y acelerar tu operación",
  },
  {
    name: "pro_v2",
    label: "Pro",
    promotionalPrice: getPromotionalPrice(279_900, "pro_v2"),
    monthlyPromotionEligible: isMonthlyPromotionEligible("pro_v2"),
    regularPrice: 279_900,
    users: 8,
    locations: 3,
    monthlySalesCommercialLimit: 2500,
    integrations: 3,
    cuits: 1,
    exclusive: false,
    description: "Para equipos con más volumen y canales",
  },
  {
    name: "max_v2",
    label: "Max",
    promotionalPrice: getPromotionalPrice(599_900, "max_v2"),
    monthlyPromotionEligible: isMonthlyPromotionEligible("max_v2"),
    regularPrice: 599_900,
    users: 15,
    locations: 5,
    monthlySalesCommercialLimit: 5000,
    integrations: 5,
    cuits: 1,
    exclusive: true,
    description: "Para operaciones de mayor escala",
  },
] as const;

export type LegacyPricingTier = (typeof LEGACY_PRICING_TIERS)[number];
export type PublicPricingTier = (typeof PUBLIC_PRICING_TIERS)[number];
export type BillablePricingTier = LegacyPricingTier | PublicPricingTier;

export const LEGACY_COMMON_FEATURES = [
  {
    key: "users",
    icon: "users",
    getLabel: (value: number) => `${value} usuarios`,
  },
  {
    key: "locations",
    icon: "mapPin",
    getLabel: (value: number) =>
      `${value} ${value === 1 ? "ubicación" : "ubicaciones"} (locales o depósitos)`,
  },
  {
    key: "invoices",
    icon: "receipt",
    getLabel: (value: number) => `${value} ventas o compras`,
  },
  {
    key: "integrations",
    icon: "blocks",
    getLabel: (value: number) =>
      `${value} ${value === 1 ? "cuenta integrada" : "cuentas integradas"}: Mercado Libre, Tiendanube, Shopify o WooCommerce`,
  },
] as const;

export const PUBLIC_COMMON_FEATURES = [
  {
    key: "users",
    icon: "users",
    getLabel: (value: number) => `${value} usuarios activos`,
  },
  {
    key: "locations",
    icon: "mapPin",
    getLabel: (value: number) =>
      `${value} ${value === 1 ? "depósito o local" : "depósitos o locales"}`,
  },
  {
    key: "monthlySalesCommercialLimit",
    icon: "receipt",
    getLabel: (value: number) =>
      `Hasta ${value.toLocaleString("es-AR")} ventas mensuales`,
  },
  {
    key: "integrations",
    icon: "blocks",
    getLabel: (value: number) =>
      `${value} ${value === 1 ? "cuenta ecommerce" : "cuentas ecommerce"}`,
  },
  {
    key: "cuits",
    icon: "building",
    getLabel: (value: number) => `${value} CUIT`,
  },
] as const;

export function getFeatureIcon(iconType: string) {
  switch (iconType) {
    case "users":
      return "Users";
    case "mapPin":
      return "MapPin";
    case "receipt":
      return "Receipt";
    case "blocks":
      return "Blocks";
    case "building":
      return "Building2";
    default:
      return "Check";
  }
}

export function getAnnualPrice(monthlyPrice: number): number {
  return Math.round(monthlyPrice * (1 - PROMOTIONAL_CONSTANTS.annualDiscount));
}

export function getDisplayPrice(
  price: number,
  billingPeriod: BillingPeriod
): number {
  return billingPeriod === "annual" ? getAnnualPrice(price) : price;
}

export function getLegacyPlanByName(
  name: LegacySelfServePlanType
): LegacyPricingTier {
  const plan = LEGACY_PRICING_TIERS.find((tier) => tier.name === name);
  if (!plan) {
    throw new Error(`Unknown legacy pricing plan "${name}"`);
  }
  return plan;
}

export function getPublicPlanByName(
  name: PublicSelfServePlanType
): PublicPricingTier {
  const plan = PUBLIC_PRICING_TIERS.find((tier) => tier.name === name);
  if (!plan) {
    throw new Error(`Unknown public pricing plan "${name}"`);
  }
  return plan;
}

export function getBillablePlanByName(
  name: Exclude<BillingPlanType, "enterprise">
): BillablePricingTier {
  if (name === "starter" || name === "pro" || name === "max") {
    return getLegacyPlanByName(name);
  }
  return getPublicPlanByName(name);
}

export function getPromotionalText() {
  const discountPercent = Math.round(
    PROMOTIONAL_CONSTANTS.promotionalDiscount * 100
  );
  return {
    discountPercent,
    promotionalMonths: PROMOTIONAL_CONSTANTS.promotionalMonths,
    trialDays: PROMOTIONAL_CONSTANTS.trialDays,
    annualDiscountPercent: Math.round(
      PROMOTIONAL_CONSTANTS.annualDiscount * 100
    ),
  };
}
